import { z } from 'zod';
import type { ApiClient } from './ApiClient.js';
import type { ApiContractModule, ApiEndpointContract, ApiResponseStyle } from '@cnapse/shared/api/contract.types.js';
import type { BlobAttachmentPayload } from './ApiClient.js';

type ContractCallArgs = {
  pathParams?: Record<string, unknown>;
  query?: Record<string, unknown>;
  body?: unknown;
  onUploadProgress?: (percent: number) => void;
};

function buildPath(template: string, pathParams: Record<string, unknown>): string {
  return template.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => {
    const value = pathParams[key];
    if (value === undefined || value === null) {
      throw new Error(`Missing path param: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

function appendQuery(
  path: string,
  query: Record<string, unknown> | undefined,
  queryLimitMax?: number,
): string {
  if (!query) return path;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (value instanceof Date) {
      params.append(key, value.toISOString());
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
      continue;
    }
    if (key === 'limit' && typeof value === 'number' && queryLimitMax != null) {
      params.append(key, String(Math.min(value, queryLimitMax)));
      continue;
    }
    params.append(key, String(value));
  }

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function normalizePathParamsInput(
  schema: z.ZodTypeAny,
  value: unknown,
): Record<string, unknown> {
  if (value === undefined || value === null) {
    throw new Error('Path parameter is required');
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const shape = (schema as z.ZodObject<z.ZodRawShape>).shape;
    const keys = Object.keys(shape);
    if (keys.length === 1) {
      return { [keys[0]]: value };
    }
  }
  return schema.parse(value) as Record<string, unknown>;
}

function isBrowserFile(value: unknown): value is File {
  const FileCtor = (globalThis as { File?: new (...args: unknown[]) => object }).File;
  return typeof FileCtor === 'function' && value instanceof FileCtor;
}

function buildFormData(body: Record<string, unknown>): FormData {
  const formData = new FormData();
  for (const [key, value] of Object.entries(body)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (isBrowserFile(value)) {
      formData.append(key, value);
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length > 0 && isBrowserFile(value[0])) {
        value.forEach((file) => formData.append(key, file));
      } else {
        value.forEach((item) => formData.append(key, String(item)));
      }
      continue;
    }
    if (typeof value === 'boolean') {
      formData.append(key, String(value));
      continue;
    }
    formData.append(key, String(value));
  }
  return formData;
}

function parseFilenameFromDisposition(
  contentDisposition: string | null | undefined,
  fallback: string,
): string {
  const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/);
  return filenameMatch?.[1] ?? fallback;
}

function parseResponse(
  contract: ApiEndpointContract,
  raw: unknown,
): unknown {
  const style: ApiResponseStyle = contract.responseStyle ?? 'unwrap-data';

  if (style === 'blob') {
    return raw;
  }

  if (style === 'blob-attachment') {
    const { blob, contentDisposition } = raw as BlobAttachmentPayload;
    const fallback =
      contract.attachmentFallbackFilename ??
      `download-${new Date().toISOString().slice(0, 10)}`;
    return {
      blob,
      filename: parseFilenameFromDisposition(contentDisposition, fallback),
    };
  }

  if (style === 'void' && (raw === null || raw === undefined)) {
    return undefined;
  }

  const parsed = contract.response.parse(raw);

  if (style === 'parse-response') {
    return parsed;
  }

  if (style === 'void') {
    const envelope = parsed as { success?: boolean; message?: string };
    if (!envelope.success) {
      throw new Error(envelope.message || contract.errorMessage || 'Request failed');
    }
    return undefined;
  }

  if (style === 'unwrap-data-field') {
    const envelope = parsed as {
      success?: boolean;
      message?: string;
      data?: Record<string, unknown>;
    };
    if (!envelope.success) {
      throw new Error(envelope.message || contract.errorMessage || 'Request failed');
    }
    const field = contract.dataField;
    if (!field || !envelope.data || envelope.data[field] === undefined) {
      throw new Error(contract.errorMessage || 'Request failed');
    }
    return envelope.data[field];
  }

  const envelope = parsed as { success?: boolean; message?: string; data?: unknown };
  if (!envelope.success) {
    throw new Error(envelope.message || contract.errorMessage || 'Request failed');
  }
  return envelope.data;
}

async function executeContract(
  client: ApiClient,
  contract: ApiEndpointContract,
  args: ContractCallArgs,
): Promise<unknown> {
  const pathParams = contract.pathParams
    ? normalizePathParamsInput(contract.pathParams, args.pathParams)
    : {};
  const query = contract.query
    ? (contract.query.parse(args.query ?? {}) as Record<string, unknown>)
    : undefined;

  let requestBody: BodyInit | undefined;
  if (contract.bodyStyle === 'form-data') {
    if (args.body instanceof FormData) {
      requestBody = args.body;
    } else if (contract.body) {
      const parsed = contract.body.parse(args.body) as Record<string, unknown>;
      requestBody = buildFormData(parsed);
    }
  } else {
    const body = contract.body ? contract.body.parse(args.body) : undefined;
    requestBody = body !== undefined ? JSON.stringify(body) : undefined;
  }

  const pathWithParams = buildPath(contract.path, pathParams);
  const url = appendQuery(pathWithParams, query, contract.queryLimitMax);

  if (contract.uploadProgress && requestBody instanceof FormData) {
    const raw = await client.uploadFormDataWithProgress<unknown>(url, requestBody, {
      method: contract.method,
      timeout: contract.timeoutMs,
      base: contract.base,
      skipAuthRefresh: contract.skipAuthRefresh,
      onUploadProgress: args.onUploadProgress,
    });
    return parseResponse(contract, raw);
  }

  const responseType =
    contract.responseStyle === 'blob-attachment'
      ? 'blob-attachment'
      : contract.responseStyle === 'blob'
        ? 'blob'
        : 'json';

  const raw = await client.request<unknown>(url, {
    method: contract.method,
    body: requestBody,
    timeout: contract.timeoutMs,
    base: contract.base,
    skipAuthRefresh: contract.skipAuthRefresh,
    responseType,
  });

  return parseResponse(contract, raw);
}

function createEndpointCaller(
  client: ApiClient,
  contract: ApiEndpointContract,
  inflight?: Map<string, Promise<unknown>>,
): (...callArgs: unknown[]) => Promise<unknown> {
  return (...callArgs: unknown[]) => {
    const args: ContractCallArgs = {};
    let normalizedCallArgs = callArgs;

    if (contract.uploadProgress && callArgs.length > 0 && typeof callArgs[callArgs.length - 1] === 'function') {
      args.onUploadProgress = callArgs[callArgs.length - 1] as (percent: number) => void;
      normalizedCallArgs = callArgs.slice(0, -1);
    }

    if (contract.pathParams && contract.query && contract.body) {
      args.pathParams = normalizedCallArgs[0] as Record<string, unknown>;
      args.query = normalizedCallArgs[1] as Record<string, unknown> | undefined;
      args.body = normalizedCallArgs[2];
    } else if (contract.pathParams && contract.body) {
      args.pathParams = normalizedCallArgs[0] as Record<string, unknown>;
      args.body = normalizedCallArgs[1];
    } else if (contract.pathParams && contract.query) {
      args.pathParams = normalizedCallArgs[0] as Record<string, unknown>;
      args.query = normalizedCallArgs[1] as Record<string, unknown> | undefined;
    } else if (contract.pathParams) {
      args.pathParams = normalizedCallArgs[0] as Record<string, unknown>;
    } else if (contract.body) {
      args.body = normalizedCallArgs[0];
    } else if (contract.query) {
      args.query = normalizedCallArgs[0] as Record<string, unknown> | undefined;
    }

    const run = () => executeContract(client, contract, args);

    if (contract.dedupe && inflight) {
      const key = JSON.stringify(args);
      const existing = inflight.get(key);
      if (existing) return existing;
      const promise = run().finally(() => inflight.delete(key));
      inflight.set(key, promise);
      return promise;
    }

    return run();
  };
}

/** Build a callable API module from a shared contract definition. */
export function createContractClient<T extends ApiContractModule>(
  client: ApiClient,
  contractModule: T,
): { [K in keyof T]: (...args: unknown[]) => Promise<unknown> } {
  const inflight = new Map<string, Promise<unknown>>();
  const result = {} as { [K in keyof T]: (...args: unknown[]) => Promise<unknown> };

  for (const [name, contract] of Object.entries(contractModule) as [keyof T, ApiEndpointContract][]) {
    result[name] = createEndpointCaller(client, contract, inflight);
  }

  return result;
}
